/**
 * 多笔充值队列：submitting → depositing → success/failed
 * 每笔独立 receipt 轮询，incoming 扫链可并行追加条目。
 */

export const DEPOSIT_ITEMS_STORAGE_KEY = 'deposit-items-queue-v1';
export const PANEL_SEEN_STORAGE_KEY = 'deposit-panel-seen-v3';

export const ITEM_PHASE = {
  SUBMITTING: 'submitting',
  DEPOSITING: 'depositing',
  SUCCESS: 'success',
  FAILED: 'failed',
};

export function createDepositItemId() {
  return `dep_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

export function normalizeItemKey(value) {
  const raw = String(value ?? '')
    .trim()
    .toLowerCase();
  return raw || '';
}

export function formatDepositAmountLabel(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n) || n <= 0) return '';
  return n >= 1 ? n.toFixed(2) : n.toFixed(4);
}

export function isItemInProgress(phase) {
  return phase === ITEM_PHASE.SUBMITTING || phase === ITEM_PHASE.DEPOSITING;
}

export function isItemTerminal(phase) {
  return phase === ITEM_PHASE.SUCCESS || phase === ITEM_PHASE.FAILED;
}

export function loadDepositItemsSnapshot() {
  try {
    const raw = sessionStorage.getItem(DEPOSIT_ITEMS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function saveDepositItemsSnapshot(items) {
  try {
    const active = items.filter(
      (item) =>
        isItemInProgress(item.phase) ||
        (isItemTerminal(item.phase) && !item.navDismissed && !item.embeddedSeen)
    );
    if (!active.length) {
      sessionStorage.removeItem(DEPOSIT_ITEMS_STORAGE_KEY);
      return;
    }
    sessionStorage.setItem(DEPOSIT_ITEMS_STORAGE_KEY, JSON.stringify(active));
  } catch {
    // ignore
  }
}

/** 登出：清空充值队列与面板已读标记 */
export function clearDepositItemsStorage() {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.removeItem(DEPOSIT_ITEMS_STORAGE_KEY);
    sessionStorage.removeItem(PANEL_SEEN_STORAGE_KEY);
  } catch {
    // ignore
  }
}

export function itemSeenKey(item) {
  if (!item) return '';
  return normalizeItemKey(item.txHash) || normalizeItemKey(item.id);
}

/**
 * @param {'cobo'|'direct'|'connected'} [depositRail]
 * cobo：用户→Cobo→Privy，仅订单 status=5 可 SUCCESS
 */
export function buildDepositItem({
  id,
  phase = ITEM_PHASE.SUBMITTING,
  txHash = '',
  amount,
  tokenSymbol = 'USDC',
  watchAddress = '',
  fromAddress = '',
  chainId,
  explorerUrl = '',
  completionExplorerUrl = '',
  startedWithDialogOpen = false,
  uiSurface = 'nav',
  source = 'connected',
  depositRail = 'direct',
  coboAddress = '',
  privyAddress = '',
  coboOrderId = '',
  coboOrderStatus = null,
  coboStatusText = '',
  exchangeStatus = null,
  completionTxHash = '',
  coboOnChainConfirmed = false,
  coboOrderCreatedAt = null,
  createdAt = Date.now(),
  detectedAt = null,
  /** 充值交易出块时间（链上 ms） */
  depositConfirmedAt = null,
  confirmedAt = null,
} = {}) {
  const rail = depositRail === 'cobo' || depositRail === 'connected' ? depositRail : 'direct';
  return {
    id: id || createDepositItemId(),
    phase,
    txHash: String(txHash ?? '').trim(),
    amount,
    amountLabel: formatDepositAmountLabel(amount),
    tokenSymbol: String(tokenSymbol ?? 'USDC').trim() || 'USDC',
    watchAddress: String(watchAddress ?? '').trim(),
    fromAddress: String(fromAddress ?? '').trim(),
    chainId,
    explorerUrl: String(explorerUrl ?? '').trim(),
    completionExplorerUrl: String(completionExplorerUrl ?? '').trim(),
    startedWithDialogOpen: Boolean(startedWithDialogOpen),
    uiSurface: uiSurface === 'embedded' ? 'embedded' : 'nav',
    source,
    depositRail: rail,
    coboAddress: String(coboAddress || (rail === 'cobo' ? watchAddress : '') || '').trim(),
    privyAddress: String(privyAddress ?? '').trim(),
    coboOrderId: String(coboOrderId ?? '').trim(),
    coboOrderStatus,
    coboStatusText: String(coboStatusText ?? '').trim(),
    exchangeStatus,
    completionTxHash: String(completionTxHash ?? '').trim(),
    coboOnChainConfirmed: Boolean(coboOnChainConfirmed),
    coboOrderCreatedAt: coboOrderCreatedAt ?? null,
    navDisplayed: false,
    navDismissed: false,
    embeddedSeen: false,
    createdAt,
    updatedAt: createdAt,
    detectedAt: detectedAt ?? (phase === ITEM_PHASE.DEPOSITING ? createdAt : null),
    depositConfirmedAt: depositConfirmedAt ?? null,
    confirmedAt: confirmedAt ?? (phase === ITEM_PHASE.SUCCESS ? createdAt : null),
  };
}

export function isCoboDepositItem(item) {
  return item?.depositRail === 'cobo';
}

export function hasInProgressItems(items) {
  return items.some((item) => isItemInProgress(item.phase));
}

function itemPriorityRank(item) {
  if (isItemInProgress(item.phase)) return 3;
  if (item.phase === ITEM_PHASE.SUCCESS) return 2;
  if (item.phase === ITEM_PHASE.FAILED) return 1;
  return 0;
}

/** 同一 txHash 只保留优先级最高的一条，避免重复通知 */
export function dedupeDepositItemsByTxHash(items) {
  const byHash = new Map();
  const withoutHash = [];

  for (const item of items) {
    const hash = normalizeItemKey(item.txHash);
    if (!hash) {
      withoutHash.push(item);
      continue;
    }
    const prev = byHash.get(hash);
    if (!prev) {
      byHash.set(hash, item);
      continue;
    }
    const rankDiff = itemPriorityRank(item) - itemPriorityRank(prev);
    if (rankDiff > 0) {
      byHash.set(hash, item);
      continue;
    }
    if (
      rankDiff === 0 &&
      (item.updatedAt ?? item.createdAt ?? 0) > (prev.updatedAt ?? prev.createdAt ?? 0)
    ) {
      byHash.set(hash, item);
    }
  }

  return [...withoutHash, ...byHash.values()];
}

export function collectTxHashesFromItems(items) {
  const set = new Set();
  for (const item of items) {
    const hash = normalizeItemKey(item.txHash);
    if (hash) set.add(hash);
  }
  return [...set];
}

export function mergeSeenTxLists(...lists) {
  const set = new Set();
  for (const list of lists) {
    if (!Array.isArray(list)) continue;
    for (const raw of list) {
      const hash = normalizeItemKey(raw);
      if (hash) set.add(hash);
    }
  }
  return [...set];
}

export function listVisibleQueueItems(items) {
  return items
    .filter((item) => {
      if (item.navDismissed && item.embeddedSeen) return false;
      return (
        isItemInProgress(item.phase) ||
        (isItemTerminal(item.phase) && (!item.navDismissed || !item.embeddedSeen))
      );
    })
    .sort((a, b) => (b.updatedAt ?? b.createdAt ?? 0) - (a.updatedAt ?? a.createdAt ?? 0));
}

export function countDepositItems(items) {
  let confirming = 0;
  let pendingWallet = 0;
  let recentSuccess = 0;

  for (const item of items) {
    if (isItemInProgress(item.phase)) {
      confirming += 1;
      if (item.phase === ITEM_PHASE.SUBMITTING) pendingWallet += 1;
    } else if (item.phase === ITEM_PHASE.SUCCESS && !item.navDismissed) {
      recentSuccess += 1;
    }
  }

  return { confirming, pendingWallet, recentSuccess };
}

/** 焦点：最新需用户感知的条目 */
export function pickFocusItemId(items) {
  if (!items.length) return null;

  const visible = items.filter((item) => !(item.navDismissed && item.embeddedSeen));
  if (!visible.length) return null;

  const rank = (item) => {
    if (isItemInProgress(item.phase)) return 3;
    if (item.phase === ITEM_PHASE.SUCCESS) return 2;
    if (item.phase === ITEM_PHASE.FAILED) return 1;
    return 0;
  };

  const sorted = [...visible].sort((a, b) => {
    const rankDiff = rank(b) - rank(a);
    if (rankDiff !== 0) return rankDiff;
    return (b.updatedAt ?? b.createdAt ?? 0) - (a.updatedAt ?? a.createdAt ?? 0);
  });

  return sorted[0]?.id ?? null;
}

/** Nav 展示条目：nav 面且未 dismiss */
export function pickNavItem(items, focusItem) {
  const candidates = items.filter(
    (item) =>
      item.uiSurface === 'nav' &&
      !item.navDismissed &&
      (isItemInProgress(item.phase) ||
        item.phase === ITEM_PHASE.SUCCESS ||
        item.phase === ITEM_PHASE.FAILED)
  );

  if (!candidates.length) {
    if (
      focusItem &&
      !focusItem.navDismissed &&
      (isItemInProgress(focusItem.phase) || isItemTerminal(focusItem.phase))
    ) {
      return focusItem;
    }
    return null;
  }

  if (focusItem && candidates.some((item) => item.id === focusItem.id)) {
    return focusItem;
  }

  candidates.sort((a, b) => (b.updatedAt ?? b.createdAt ?? 0) - (a.updatedAt ?? a.createdAt ?? 0));
  return candidates[0];
}

export function shouldShowEmbeddedForItem(item, dialogOpen, allItems = []) {
  if (!item || !dialogOpen) return false;
  if (item.uiSurface !== 'embedded') return false;
  if (item.embeddedSeen) return false;
  if (isItemTerminal(item.phase) && hasInProgressItems(allItems)) return false;
  return isItemInProgress(item.phase) || isItemTerminal(item.phase);
}

export function shouldShowNavForItem(item, dialogOpen) {
  if (!item || item.navDismissed) return false;
  if (item.uiSurface === 'embedded' && dialogOpen) return false;
  // 终态已在 Embedded 展示过 → 不再 Nav；进行中关窗后由 uiSurface 切到 nav 继续展示
  if (item.uiSurface === 'embedded' && item.navDisplayed && !isItemInProgress(item.phase)) {
    return false;
  }
  if (!isItemInProgress(item.phase) && !isItemTerminal(item.phase)) return false;
  return true;
}

/** UI phase：submitting 单独展示文案 */
export function itemUiPhase(item) {
  if (!item) return 'idle';
  return item.phase;
}
